import React, { useEffect, useState } from 'react'
import { Box, Button, Container, Flex, Grid, Text } from '@chakra-ui/react'
import Link from 'next/link'
import { useNavbarLogin } from '@/lib/useNavbarLogin'
import { MenuLanding } from './MenuLanding'
import Image from 'next/image'
import posthog from 'posthog-js'
import tenant from '@/lib/tenant'
import tenantChrome from '@/lib/tenantChrome'
import type { SiteNavigationData } from '@/lib/siteNavigation'
import LocaleSwitcher from '@/components/LocaleSwitcher'
import { useTranslations } from 'next-intl'

const useIsScrolled = (scrollRef: React.RefObject<HTMLDivElement>) => {
	const [isScrolled, setIsScrolled] = useState(false)

	useEffect(() => {
		const currentRef = scrollRef?.current
		const handleScroll = () => {
			if (!currentRef) return
			const scrollPosition = currentRef.scrollTop
			setIsScrolled(scrollPosition > 100)
		}
		currentRef?.addEventListener('scroll', handleScroll)
		return () => currentRef?.removeEventListener('scroll', handleScroll)
	}, [scrollRef])

	return isScrolled
}

const NavbarLanding = ({
	data,
	siteNavigation,
	scrollRef,
}: {
	data: any
	siteNavigation?: SiteNavigationData
	scrollRef: React.RefObject<HTMLDivElement>
}) => {
	const login = useNavbarLogin()
	const t = useTranslations('nav')
	const isLoggedIn = login.signedIn
	const isScrolled = useIsScrolled(scrollRef)

	if (!data) return null
	const logo = data.logo || siteNavigation?.logoUrl || tenant.brand.logoSrc
	const isSwitcherVisible = siteNavigation?.languageSwitcherVisible ?? true
	// Studio's header tagline wins, then the tenant bundle's own. Deliberately
	// NOT siteNavigation.tagline — that field is the SEO meta description, a
	// full sentence that would blow out this nowrap line. And deliberately no
	// appTitle fallback: the logo beside this text already carries the company
	// name, so falling back to it printed the name twice. Blank means "no
	// tagline configured" — render the logo alone, not a duplicate.
	const tagline = siteNavigation?.headerTagline || tenant.brand.tagline || ''
	const switcherPosition = siteNavigation?.languageSwitcherPosition ?? 'right'

	const handleLogoClick = () => {
		posthog.capture('home_button_header')
	}

	const handleLoginClick = () => {
		posthog.capture('log_in_header')
	}

	const handlePosthogClick = ({
		posthogEvent,
	}: {
		posthogEvent: string | undefined
	}) => {
		if (posthogEvent) {
			posthog.capture(posthogEvent)
		}
	}

	return (
		<>
			<Box
				minH={{ base: 12, md: 20 }}
				zIndex={11}
				position='fixed'
				top={0}
				left={0}
				right={0}
				bg={isScrolled ? 'whiteAlpha.900' : 'transparent'}
				backdropFilter={isScrolled ? 'blur(20px)' : 'none'}
				shadow={isScrolled ? 'sm' : 'none'}
				transition='all 0.4s ease'
			>
				<Container maxW='container.xl'>
					<Grid templateColumns='1fr 1fr' minH={{ base: 12, md: 20 }}>
						<Flex alignItems='center' gap={6}>
							<Box
								as={Link}
								href='/'
								flexShrink={0}
								ms={{ base: 0, md: 2 }}
								h={{ base: '6', md: '9' }}
								onClick={handleLogoClick}
							>
								<Image
									src={logo}
									width='263'
									height='48'
									alt={tenant.brand.appTitle}
									style={{ height: '100%', width: 'auto' }}
									priority
								/>
							</Box>
							{tagline && (
								<Text
									color='primary.800'
									mt={0.5}
									fontWeight='medium'
									display={{ base: 'none', md: 'block' }}
									opacity={isScrolled ? 1 : 0}
									transition='opacity 0.4s ease'
									whiteSpace='nowrap'
								>
									{tagline}
								</Text>
							)}
							{switcherPosition === 'left' && (
								<LocaleSwitcher visible={isSwitcherVisible} position='left' />
							)}
						</Flex>
						<Flex
							justifyContent='space-between'
							alignItems='center'
							borderBottom={isScrolled ? 'none' : '1px solid'}
							borderBottomColor={{ base: 'transparent', md: 'blackAlpha.200' }}
							ms={4}
						>
							{tagline && (
								<Text
									color='primary.800'
									fontWeight='medium'
									display={{ base: 'none', lg: 'block' }}
									opacity={isScrolled ? 0 : 1}
									transition='opacity 0.4s ease'
									whiteSpace='nowrap'
								>
									{tagline}
								</Text>
							)}
							{switcherPosition === 'center' && (
								<Flex justifyContent='center' flex={1}>
									<LocaleSwitcher
										visible={isSwitcherVisible}
										position='center'
									/>
								</Flex>
							)}
							{/* Both opt-outs come from the tenant's config.chrome: a tenant
							    with no customer portal hides Login, and one with no
							    landing.navigation.primary hides the menu rather than
							    shipping a hamburger that opens an empty popover. */}
							<Flex gap={{ base: 2, md: 6 }} ms='auto' alignItems='center'>
								{switcherPosition === 'right' && (
									<LocaleSwitcher
										visible={isSwitcherVisible}
										position='right'
									/>
								)}
								{tenantChrome.siteMenu &&
									(siteNavigation?.navPrimary?.length ?? 0) > 0 && (
										<Flex
											display={{ base: 'none', md: 'flex' }}
											gap={6}
											alignItems='center'
										>
											{siteNavigation!.navPrimary.map(item => (
												<Box
													key={item.label}
													as={Link}
													href={item.href}
													color='primary.800'
													fontWeight='semibold'
													whiteSpace='nowrap'
													_hover={{ color: 'primary.600' }}
													onClick={() =>
														handlePosthogClick({
															posthogEvent: item.posthogEvent,
														})
													}
												>
													{item.label}
												</Box>
											))}
										</Flex>
									)}
								{tenantChrome.login && (
									<Button
										as={Link}
										href={login.href}
										variant='solid'
										borderRadius='full'
										size={{ base: 'sm', md: 'md' }}
										onClick={handleLoginClick}
									>
										{isLoggedIn ? t('dashboard') : t('login')}
									</Button>
								)}
								{tenantChrome.siteMenu && (
									<Box display={{ base: 'flex', md: 'none' }}>
										<MenuLanding items={siteNavigation?.navPrimary} />
									</Box>
								)}
							</Flex>
						</Flex>
					</Grid>
				</Container>
			</Box>
		</>
	)
}

export default NavbarLanding
